import React, { memo, Suspense, useCallback, useMemo, useState } from 'react'
import { HStack } from '../../../../../../ui/Stack'
import { Modal } from 'antd'
import { ModalCancel } from '../../../../../../views/modal-cancel'
import { useSelector } from 'react-redux'
import { getTimeLineData } from '../../../../../../store/timeline/action-creators'
import { getStagePlanData } from '../../../../../../store/stagePlan/action-creators'
import {
  getClaimFormLoading,
  getVisibleFormData,
  selectClaimStages,
  createSession,
} from '../../../selectors'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import { getClaimData } from '../../../../../../store/claim/action-creators'
import { ModalCreateTask } from 'src/react-app/views/modal-create-task-new'
import { ModalAddNote } from '../../../../../../views/modal-add-notes-new'
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch/useAppDispatch'
import { WorksModal } from '../../modals/modal-works'
import { ClaimPrintPrepareModal } from '../../modals/claim-print-prepare'
import {
  CardActions,
  useCardActions,
  CardActionConfig,
} from '../../../../../../components/v2/card-actions'
import { HSteps } from '../../../../../../ui/Steps'
import {
  EditOutlined,
  PlusOutlined,
  CloseOutlined,
  ToolOutlined,
  PrinterOutlined,
} from '@ant-design/icons'
import { hasPermission } from 'src/react-app/lib/permissions'
import { Permissions } from 'src/react-app/constants/permissions'

type TStage = 'work' | 'completed' | 'cancelled'

interface FormsHeaderProps {
  id: string | number
  nextStageOnClick?: (stage: TStage) => Promise<void>
  showTitle?: boolean
}

interface ISelTemp {
  id: number
  name: string
}

export const FormsHeader: React.FC<FormsHeaderProps> = memo((props: FormsHeaderProps) => {
  const { id, nextStageOnClick } = props
  const [isOpenModalCancel, setIsOpenModalCancel] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const visibleFormData = useSelector(getVisibleFormData)
  const stages = useSelector(selectClaimStages)
  const { sessionData } = useSelector(createSession)
  const [isOpenModalCreateTask, setIsOpenModalCreateTask] = useState<boolean>(false)
  const [isOpenModalAddNote, setIsOpenModalAddNote] = useState<boolean>(false)
  const [isOpenModalWorks, setIsOpenModalWorks] = useState<boolean>(false)
  const [isOpenModalPrint, setIsOpenModalPrint] = useState<boolean>(false)
  const [selectedTemplate, setSelectedTemplate] = useState<null | ISelTemp>(null)
  const claimFormIsLoading = useSelector(getClaimFormLoading)
  const templates = visibleFormData?.templates ?? []

  const openPrepareModal = useCallback((tpl: ISelTemp) => {
    setSelectedTemplate(tpl)
    setIsOpenModalPrint(true)
  }, [])

  const onClosePrint = useCallback(() => {
    setIsOpenModalPrint(false)
    setSelectedTemplate(null)
  }, [])

  const handleCancelOnClick = useCallback(() => {
    if (visibleFormData) {
      const isRequiredFields = isFormDataRequired(
        visibleFormData?.resultObject,
        visibleFormData?.resultSchema,
      )
      if (!isRequiredFields) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      }
    }
    setIsOpenModalCancel(true)
  }, [visibleFormData])

  const onCloseCancel = useCallback(() => {
    setIsOpenModalCancel(false)
  }, [setIsOpenModalCancel])

  const onCloseAddNote = useCallback(() => {
    setIsOpenModalAddNote(false)
  }, [setIsOpenModalAddNote])

  const onCancel = useCallback(async () => {
    await dispatch(getClaimData(id))
    dispatch(getTimeLineData('claim', id))
    dispatch(getStagePlanData('claim', id))
    setIsOpenModalCancel(false)
  }, [id, dispatch])

  const canManageClaims = useMemo(
    () => hasPermission(sessionData?.permissionsWithScope, Permissions.ACCESS_CLAIMS),
    [sessionData?.permissionsWithScope],
  )

  const buttonsDisabled = useMemo(() => {
    return !(
      stages?.items !== undefined &&
      stages?.current !== undefined &&
      canManageClaims
    )
  }, [stages, canManageClaims])

  const disabledButtons = useMemo(() => {
    if (!stages?.items || typeof stages?.current !== 'number') {
      return true
    }
    const currentStageCode = stages?.items?.[stages?.current]?.code
    if (
      currentStageCode &&
      canManageClaims &&
      !['completed', 'cancelled'].includes(currentStageCode)
    ) {
      return false
    }
    return !claimFormIsLoading
  }, [stages, canManageClaims, claimFormIsLoading])

  const currentStageCode = stages?.items?.[stages?.current]?.code || null

  const onCloseCreateTask = useCallback(() => {
    setIsOpenModalCreateTask(false)
  }, [])

  const handleToWork = useCallback(async () => {
    await nextStageOnClick?.('work')
  }, [nextStageOnClick])

  const handleToCompleted = useCallback(async () => {
    await nextStageOnClick?.('completed')
  }, [nextStageOnClick])

  const currentStage = stages?.current ?? null

  const actionsConfig = useMemo(
    (): CardActionConfig[] => [
      {
        key: 'to-work',
        label: 'В работу',
        type: 'primary',
        onClick: handleToWork,
        isPrimary: true,
        disabled: buttonsDisabled,
        condition: currentStage === 0,
        order: 1,
      },
      {
        key: 'to-completed',
        label: 'Закрыть',
        type: 'primary',
        onClick: handleToCompleted,
        isPrimary: true,
        disabled: buttonsDisabled,
        condition: currentStage === 1,
        order: 1,
      },
      {
        key: 'assign-works',
        label: 'Назначить работы',
        icon: <ToolOutlined />,
        onClick: () => setIsOpenModalWorks(true),
        condition: currentStageCode === 'work',
        disabled: disabledButtons,
        order: 5,
      },
      {
        key: 'add-note',
        label: 'Добавить заметку',
        icon: <EditOutlined />,
        onClick: () => setIsOpenModalAddNote(true),
        condition: true,
        order: 10,
      },
      {
        key: 'create-task',
        label: 'Создать задачу',
        icon: <PlusOutlined />,
        onClick: () => setIsOpenModalCreateTask(true),
        condition: true,
        order: 20,
      },
      {
        key: 'print',
        label: 'Печать',
        icon: <PrinterOutlined />,
        type: 'dropdown',
        menuItems: templates.map(tpl => ({
          key: `template-${tpl.id}`,
          label: tpl.name,
          onClick: () => openPrepareModal(tpl),
        })),
        condition: Boolean(templates?.length),
        order: 90,
      },
      {
        key: 'cancel',
        label: 'Отменить',
        icon: <CloseOutlined />,
        danger: true,
        onClick: handleCancelOnClick,
        disabled: disabledButtons,
        condition: true,
        order: 100,
      },
    ],
    [
      currentStage,
      currentStageCode,
      buttonsDisabled,
      disabledButtons,
      handleToWork,
      handleToCompleted,
      handleCancelOnClick,
      templates,
      openPrepareModal,
    ],
  )

  const { primaryAction, secondaryActions } = useCardActions({ actions: actionsConfig })

  return (
    <>
      <HStack gap={'8'} className={'mapWrapper'} wrap>
        <div>
          {stages?.items && typeof stages?.current === 'number' && stages?.history && (
            <HSteps
              items={stages?.items}
              current={stages?.current || 0}
              history={stages?.history}
            />
          )}
        </div>
        <CardActions primaryAction={primaryAction} secondaryActions={secondaryActions} />
      </HStack>

      <Suspense fallback={''}>
        <ModalCreateTask
          isOpen={isOpenModalCreateTask}
          onClose={onCloseCreateTask}
          entityType={'claim'}
          entityId={id}
        />
      </Suspense>

      <ModalAddNote
        entityId={Number(id)}
        entityType={'claim'}
        isOpen={isOpenModalAddNote}
        onClose={onCloseAddNote}
      />

      <WorksModal
        id={id}
        isModalOpen={isOpenModalWorks}
        onCancel={() => setIsOpenModalWorks(false)}
      />

      <Suspense fallback={''}>
        <ModalCancel
          entityId={id}
          entityType="claim"
          isOpen={isOpenModalCancel}
          onClose={onCloseCancel}
          onCancel={onCancel}
          title={'Отмена рекламации'}
        />
      </Suspense>

      <ClaimPrintPrepareModal
        id={id}
        template={selectedTemplate}
        isModalPrintOpen={isOpenModalPrint}
        onCancel={onClosePrint}
      />
    </>
  )
})

FormsHeader.displayName = 'ClaimFormsHeader'
